/**
 * ResultRegularizerModal
 *
 * 3-step wizard:
 *   1. Upload — pick a file (image or PDF)
 *   2. Extracting — backend AI reads the document
 *   3. Review & Confirm — user edits the extracted table, then submits
 */

import { useState, useRef } from "react";
import {
  X, Upload, FileText, AlertCircle, Loader2,
  CheckCircle2, Plus, Trash2, ChevronRight,
} from "lucide-react";
import api from "@/hooks/api/apiClient";
import { StorageService } from "@/hooks/services/storageService";
import { AuthStore } from "@/hooks/stores/AuthStore";
import config from "../../../../config";

// ─── Types ────────────────────────────────────────────────────────────────────

type ResultType = "waec" | "neco" | "nabteb" | "jamb" | "school_report" | "other";

interface ExtractedSubject {
  subject: string;
  grade: string | null;
  score: number | null;
}

interface ExtractedData {
  examBody: ResultType | string;
  year: string | null;
  session: string | null;
  candidateName: string | null;
  candidateNumber: string | null;
  classLevel: string | null;
  school: string | null;
  subjects: ExtractedSubject[];
}

type Step = "upload" | "extracting" | "review";

interface ResultRegularizerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const EXAM_LABELS: Record<string, string> = {
  waec: "WAEC",
  neco: "NECO",
  nabteb: "NABTEB",
  jamb: "JAMB / UTME",
  school_report: "School Report",
  other: "Other",
};

function examLabel(body: string | null): string {
  if (!body) return "—";
  return EXAM_LABELS[body.toLowerCase()] ?? body;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StepDots({ step }: { step: Step }) {
  const steps: Step[] = ["upload", "extracting", "review"];
  return (
    <div className="flex items-center gap-2">
      {steps.map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <div
            className={`w-2 h-2 rounded-full transition-colors ${
              s === step ? "bg-indigo-600" : steps.indexOf(step) > i ? "bg-indigo-300" : "bg-slate-200"
            }`}
          />
          {i < steps.length - 1 && <div className="w-4 h-px bg-slate-200" />}
        </div>
      ))}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ResultRegularizerModal({ isOpen, onClose, onSaved }: ResultRegularizerModalProps) {
  const [step, setStep] = useState<Step>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [fileData, setFileData] = useState<string>("");
  const [extracted, setExtracted] = useState<ExtractedData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const fileSizeMB = file ? file.size / (1024 * 1024) : 0;
  const fileTooLarge = fileSizeMB > 5;

  // ── Step 1: pick file ──────────────────────────────────────────────────────

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0] ?? null;
    setFile(picked);
    setError(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const picked = e.dataTransfer.files?.[0] ?? null;
    if (picked) { setFile(picked); setError(null); }
  };

  const handleExtract = async () => {
    if (!file) { setError("Please select a file."); return; }
    if (fileTooLarge) { setError("File too large — max 5 MB."); return; }

    setError(null);
    setStep("extracting");

    try {
      const base64 = await fileToBase64(file);
      setFileData(base64);

      if (config.appMode === "prototype") {
        // Prototype: return mock extraction after short delay
        await new Promise((r) => setTimeout(r, 1800));
        setExtracted({
          examBody: "waec",
          year: "2024",
          session: "June/July",
          candidateName: null,
          candidateNumber: null,
          classLevel: "SS3",
          school: null,
          subjects: [
            { subject: "English Language", grade: "B2", score: 72 },
            { subject: "Mathematics", grade: "C5", score: 58 },
            { subject: "Biology", grade: "A1", score: 88 },
            { subject: "Chemistry", grade: "B3", score: 68 },
            { subject: "Physics", grade: "C4", score: 62 },
          ],
        });
      } else {
        const res = await api.post("/students/me/results/regularize", {
          fileData: base64,
          fileType: file.type,
        });
        setExtracted(res.data?.data?.extracted ?? null);
      }

      setStep("review");
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? err?.message ?? "Extraction failed. Please try again.");
      setStep("upload");
    }
  };

  // ── Step 3: edit extracted data ────────────────────────────────────────────

  const setField = <K extends keyof ExtractedData>(key: K, val: ExtractedData[K]) => {
    setExtracted((prev) => prev ? { ...prev, [key]: val } : prev);
  };

  const updateSubject = (idx: number, field: keyof ExtractedSubject, val: string) => {
    setExtracted((prev) => {
      if (!prev) return prev;
      const subjects = [...prev.subjects];
      subjects[idx] = {
        ...subjects[idx],
        [field]: field === "score" ? (val === "" ? null : Number(val)) : (val === "" ? null : val),
      };
      return { ...prev, subjects };
    });
  };

  const addSubject = () => {
    setExtracted((prev) =>
      prev ? { ...prev, subjects: [...prev.subjects, { subject: "", grade: null, score: null }] } : prev
    );
  };

  const removeSubject = (idx: number) => {
    setExtracted((prev) =>
      prev ? { ...prev, subjects: prev.subjects.filter((_, i) => i !== idx) } : prev
    );
  };

  // ── Save confirmed result ──────────────────────────────────────────────────

  const handleSave = async () => {
    if (!extracted || !fileData) return;
    setSaving(true);
    setError(null);

    try {
      const label = `${examLabel(extracted.examBody)} ${extracted.year ?? ""}`.trim();

      if (config.appMode === "prototype") {
        const user = AuthStore.getState().currentUser;
        if (!user) throw new Error("Not authenticated");
        const students = StorageService.read("students") ?? [];
        const idx = students.findIndex((s: any) => s.userId === user._id || s.email === user.email);

        const entry = {
          id: `result_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          type: extracted.examBody || "other",
          label,
          fileData,
          fileName: file?.name ?? "result",
          fileType: file?.type ?? "image/jpeg",
          extractedData: extracted,
          extractionStatus: "extracted",
          status: "pending",
          uploadedAt: new Date().toISOString(),
        };

        if (idx >= 0) {
          students[idx] = {
            ...students[idx],
            academicResults: [...(students[idx].academicResults ?? []), entry],
            updatedAt: new Date(),
          };
          StorageService.write("students", students);
        }
      } else {
        await api.post("/students/me/results", {
          extractedData: extracted,
          fileData,
          fileName: file?.name ?? "result",
          fileType: file?.type ?? "image/jpeg",
          type: extracted.examBody,
          label,
        });
      }

      onSaved?.();
      handleClose();
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? err?.message ?? "Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setStep("upload");
    setFile(null);
    setFileData("");
    setExtracted(null);
    setError(null);
    setSaving(false);
    onClose();
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={step !== "extracting" ? handleClose : undefined} />

      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100">
          <div>
            <h2 className="text-xl font-black text-slate-900">Upload Academic Results</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              {step === "upload" && "Upload your result document — we'll read it automatically."}
              {step === "extracting" && "AI is reading your document…"}
              {step === "review" && "Review the extracted data, then confirm."}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <StepDots step={step} />
            {step !== "extracting" && (
              <button onClick={handleClose} className="p-2 rounded-xl hover:bg-slate-100 transition-colors ml-2">
                <X size={18} className="text-slate-500" />
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* ── Step 1: Upload ── */}
          {step === "upload" && (
            <>
              {/* Dropzone */}
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className="cursor-pointer rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-indigo-50 hover:border-indigo-300 transition-colors p-10 flex flex-col items-center gap-3 text-center"
              >
                {file ? (
                  <>
                    <FileText size={36} className="text-indigo-500" />
                    <p className="font-semibold text-slate-800">{file.name}</p>
                    <p className="text-sm text-slate-500">{fileSizeMB.toFixed(1)} MB · Click to change</p>
                  </>
                ) : (
                  <>
                    <Upload size={36} className="text-slate-400" />
                    <p className="font-semibold text-slate-700">Drop your result here or click to browse</p>
                    <p className="text-sm text-slate-400">Supports images (JPG, PNG) and PDF — max 5 MB</p>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={handleFileChange}
              />

              {fileTooLarge && (
                <p className="flex items-center gap-1.5 text-sm text-amber-700 bg-amber-50 rounded-xl px-3 py-2.5">
                  <AlertCircle size={14} /> File is {fileSizeMB.toFixed(1)} MB — please compress below 5 MB.
                </p>
              )}

              <div className="bg-indigo-50 rounded-2xl p-4 text-sm text-indigo-700">
                <p className="font-semibold mb-1">Supported documents</p>
                <p className="text-indigo-600 text-xs leading-relaxed">
                  WAEC / NECO / NABTEB result slips, JAMB result printouts, and school report cards (any term).
                  The AI will extract all subject scores automatically.
                </p>
              </div>
            </>
          )}

          {/* ── Step 2: Extracting ── */}
          {step === "extracting" && (
            <div className="flex flex-col items-center justify-center py-16 gap-6 text-center">
              <div className="w-16 h-16 rounded-2xl bg-indigo-50 flex items-center justify-center">
                <Loader2 size={32} className="text-indigo-600 animate-spin" />
              </div>
              <div>
                <p className="font-bold text-slate-800 text-lg">Reading your document…</p>
                <p className="text-sm text-slate-500 mt-1">This usually takes 5–10 seconds.</p>
              </div>
            </div>
          )}

          {/* ── Step 3: Review ── */}
          {step === "review" && extracted && (
            <>
              {/* Meta fields */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Exam Body</label>
                  <select
                    value={extracted.examBody ?? ""}
                    onChange={(e) => setField("examBody", e.target.value)}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="waec">WAEC</option>
                    <option value="neco">NECO</option>
                    <option value="nabteb">NABTEB</option>
                    <option value="jamb">JAMB / UTME</option>
                    <option value="school_report">School Report</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Year</label>
                  <input
                    type="text"
                    value={extracted.year ?? ""}
                    onChange={(e) => setField("year", e.target.value)}
                    placeholder="2024"
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Session</label>
                  <input
                    type="text"
                    value={extracted.session ?? ""}
                    onChange={(e) => setField("session", e.target.value)}
                    placeholder="June/July"
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Class Level</label>
                  <input
                    type="text"
                    value={extracted.classLevel ?? ""}
                    onChange={(e) => setField("classLevel", e.target.value)}
                    placeholder="SS3"
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Candidate Name</label>
                  <input
                    type="text"
                    value={extracted.candidateName ?? ""}
                    onChange={(e) => setField("candidateName", e.target.value)}
                    placeholder="Full name on result"
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              {/* Subjects table */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-slate-500">Subjects ({extracted.subjects.length})</label>
                  <button
                    onClick={addSubject}
                    className="flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition-colors"
                  >
                    <Plus size={12} /> Add row
                  </button>
                </div>

                <div className="rounded-2xl border border-slate-200 overflow-hidden">
                  <div className="grid grid-cols-[1fr_80px_64px_36px] text-xs font-semibold text-slate-500 bg-slate-50 px-3 py-2 border-b border-slate-200">
                    <span>Subject</span>
                    <span>Grade</span>
                    <span>Score</span>
                    <span />
                  </div>

                  <div className="max-h-56 overflow-y-auto divide-y divide-slate-100">
                    {extracted.subjects.map((s, i) => (
                      <div key={i} className="grid grid-cols-[1fr_80px_64px_36px] items-center px-3 py-1.5 gap-2">
                        <input
                          type="text"
                          value={s.subject}
                          onChange={(e) => updateSubject(i, "subject", e.target.value)}
                          placeholder="Subject"
                          className="w-full bg-transparent text-sm text-slate-800 placeholder-slate-300 focus:outline-none focus:bg-indigo-50 rounded px-1 py-0.5"
                        />
                        <input
                          type="text"
                          value={s.grade ?? ""}
                          onChange={(e) => updateSubject(i, "grade", e.target.value)}
                          placeholder="A1"
                          className="w-full bg-transparent text-sm text-slate-800 placeholder-slate-300 focus:outline-none focus:bg-indigo-50 rounded px-1 py-0.5 text-center"
                        />
                        <input
                          type="number"
                          value={s.score ?? ""}
                          onChange={(e) => updateSubject(i, "score", e.target.value)}
                          placeholder="—"
                          className="w-full bg-transparent text-sm text-slate-800 placeholder-slate-300 focus:outline-none focus:bg-indigo-50 rounded px-1 py-0.5 text-center"
                        />
                        <button
                          onClick={() => removeSubject(i)}
                          className="p-1 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-emerald-50 rounded-2xl px-4 py-3 flex items-start gap-3">
                <CheckCircle2 size={16} className="text-emerald-600 shrink-0 mt-0.5" />
                <p className="text-xs text-emerald-700">
                  <span className="font-semibold">Review carefully.</span> Correct any errors above before confirming — a school admin will verify your document.
                </p>
              </div>
            </>
          )}

          {/* Error */}
          {error && (
            <p className="flex items-center gap-1.5 text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2.5">
              <AlertCircle size={14} /> {error}
            </p>
          )}
        </div>

        {/* Footer */}
        {step !== "extracting" && (
          <div className="flex gap-3 px-6 pb-6 pt-2 border-t border-slate-100">
            {step === "review" && (
              <button
                onClick={() => setStep("upload")}
                className="px-5 py-3 rounded-2xl border-2 border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-600 font-bold text-sm transition-all active:scale-95"
              >
                Back
              </button>
            )}
            <button
              onClick={handleClose}
              className="px-5 py-3 rounded-2xl border-2 border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-600 font-bold text-sm transition-all active:scale-95"
            >
              Cancel
            </button>
            <button
              onClick={step === "upload" ? handleExtract : handleSave}
              disabled={saving || (step === "upload" && (!file || fileTooLarge))}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold text-sm transition-all active:scale-95"
            >
              {step === "upload" ? (
                <>Extract with AI <ChevronRight size={16} /></>
              ) : saving ? (
                <><Loader2 size={14} className="animate-spin" /> Saving…</>
              ) : (
                <>Confirm & Save <CheckCircle2 size={16} /></>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
